/**
 * Quick-add-equipment modal. Sibling to AddEmployeeModal. Resolves an
 * "unknown equipment" row from NeedsAttention without leaving the PCO.
 *
 * Pre-fills:
 *   - asset_id  from the unknown row (asset ID as it appeared in the PDF,
 *               which matches the canonical asset_id Joey will keep)
 *
 * Type defaults to 'rental' — the prior pass dropped the type column from
 * the UI and made rental the default; we follow that here so this modal
 * doesn't reintroduce a question Joey shouldn't have to answer.
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { MoneyInput } from './MoneyInput';
import type { EquipmentWriteRequest, SettingsEquipment } from '@coffman-tm/shared';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

export function AddEquipmentModal({
  open,
  initialAssetId,
  initialDescription,
  onClose,
  onAdded,
}: {
  open: boolean;
  /** Asset ID from the unknown-equipment row (canonical disambiguator);
   *  empty if the parser couldn't pull one. */
  initialAssetId: string;
  /** Description text from the unknown-equipment row (the model name,
   *  often shared across multiple asset_ids). */
  initialDescription: string;
  onClose: () => void;
  onAdded: (equipment: SettingsEquipment) => void | Promise<void>;
}) {
  const { token } = useAuth();
  const [assetId, setAssetId] = useState('');
  const [desc, setDesc] = useState('');
  const [rateDollars, setRateDollars] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAssetId(initialAssetId);
    setDesc(initialDescription);
    setRateDollars('');
    setBusy(false);
    setErr(null);
  }, [open, initialAssetId, initialDescription]);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!token) return;
    if (!assetId.trim() || !desc.trim()) {
      setErr('Asset ID and description are required.');
      return;
    }
    const rate = Number(rateDollars);
    if (!Number.isFinite(rate) || rate < 0) {
      setErr('Hourly rate must be a non-negative number.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const body: EquipmentWriteRequest = {
        asset_id: assetId.trim(),
        description: desc.trim(),
        type: 'rental',
        hourly_rate_cents: Math.round(rate * 100),
        active: true,
      };
      const created = await api.settings.createEquipment(token, body);
      await onAdded(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to add equipment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Add equipment"
    >
      <div className="new-pco-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Add equipment</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          <div className="new-pco-field">
            <label className="field-label">Asset ID</label>
            <input
              className="field-input mono"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              autoFocus
              disabled={busy}
            />
          </div>
          <div className="new-pco-field">
            <label className="field-label">Description</label>
            <input
              className="field-input"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              disabled={busy}
              placeholder="e.g. 50-ton crane, Kubota mini-excavator"
            />
          </div>
          <div className="new-pco-field">
            <label className="field-label">Hourly rate (/hr)</label>
            <MoneyInput
              className="field-input mono"
              value={rateDollars}
              onChange={setRateDollars}
              disabled={busy}
            />
          </div>
          {err && (
            <div style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</div>
          )}
          <div className="new-pco-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-cta"
              onClick={submit}
              disabled={busy}
            >
              {busy ? 'Adding…' : 'Add equipment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
